import {Box, ButtonBase, Card, CircularProgress, Tooltip, Typography} from "@mui/material";
import {black, colorGreen, colorRed, nightGray, translucentBlack, white} from "@/shared/colors";
import {CallEndOutlined, CallOutlined, CloseOutlined} from "@mui/icons-material";
import {Permission, PermissionsFragment} from "@/shared/PermissionsFragment";
import {Action, ButtonState} from "@/shared/types";
import {DW_LG, PD_LG, PD_MD, PD_SM, PD_XSM, SZ_MD} from "@/shared/dimens";
import React, {ReactElement} from "react";
import {
  LocalAudioTrack,
  LocalParticipant,
  LocalTrack,
  LocalVideoTrack,
  RemoteParticipant,
  RemoteTrack,
  Room as TwilioRoom
} from "twilio-video";
import {getMemberAuth} from "@/shared/auth";
import {
  Call,
  CALL_END_REASON_TEXT_FROM_ME,
  CALL_END_REASON_TEXT_FROM_OTHER,
  CallEndReason,
  Calls,
  CallType,
  CurrentCallListener,
  RoomListener,
} from "./types";
import {
  BaseChatThreadFragment,
  BaseChatThreadFragmentProps,
  BaseChatThreadFragmentState
} from "../BaseChatThreadFragment";
import {playRingtone, stopRingtone} from "./call_utils";
import {ChatMessage} from "../chat/types";
import {BaseApp} from "@/shared/BaseApp";
import {CallAppRefs} from "../types";

export interface CallFragmentListener {
  onMinimizedStateChanged(minimized: boolean);

  onCallEnded();
}

export type CallFragmentProps = BaseChatThreadFragmentProps & {
  message: ChatMessage,
  listener: CallFragmentListener,
}

enum PermissionsState {
  GRANTED,
  DENIED,
  NOT_GRANTED,
}

type CallFragmentState = BaseChatThreadFragmentState & {
  minimized?: boolean,
  permissionsState?: PermissionsState,
  callType?: CallType,
  cameraOff?: ButtonState,
  muteOn?: ButtonState,
}

export class CallFragment extends BaseChatThreadFragment<CallFragmentProps, CallFragmentState> implements RoomListener, CurrentCallListener {

  private readonly appRefs = BaseApp.CONTEXT.getAppRefs<CallAppRefs>();
  private readonly calls = Calls.getInstance(this.props.thread.id, this);

  protected getIntrinsicHeight(): number | string {
    return null;
  }

  protected onCreateState(): CallFragmentState {
    return {
      ...super.onCreateState(),
      permissionsState: PermissionsState.NOT_GRANTED,
    };
  }

  onCallChanged(currentCall: Call | null) {
    if (!currentCall) {
      this.props.listener.onCallEnded();
    }
    this.setState({
      callType: currentCall?.type,
    });
    this.forceUpdate();
  }

  protected async fetchOnMount(forceReload?: boolean): Promise<void> {
    await this.calls.loadListItems();
  }

  protected containerContentDidRender() {
    // This check must be made and a call created after the render happens, to allow mounting of local renderers.
    if (!this.hasCall()) {
      this.calls.createCall(this.props.message.callLog.channel, this);
    }
  }

  isMinimized(): boolean {
    return this.state.minimized;
  }

  minimize() {
    this.setState({
      minimized: true,
    });
  }

  maximize() {
    this.setState({
      minimized: false
    });
  }

  hasCall(): boolean {
    return Boolean(this.calls.getCurrentCall());
  }

  onConnected(room: TwilioRoom) {
  }

  onAttachTrack(room: TwilioRoom, track: LocalTrack | RemoteTrack | null, participant: LocalParticipant | RemoteParticipant) {
    let renderer: HTMLMediaElement;
    switch (track?.kind) {
      case "audio":
        if (track instanceof LocalAudioTrack) {
          if ((renderer = this.appRefs.localAudioRenderer.current)) {
            track.attach(renderer);
          }
        } else {
          if ((renderer = this.appRefs.remoteAudioRenderer.current)) {
            track.attach(renderer);
          }
        }
        break;
      case "video":
        if (track instanceof LocalVideoTrack) {
          if ((renderer = this.appRefs.localVideoRenderer.current)) {
            track.attach(renderer);
          }
        } else {
          if ((renderer = this.appRefs.remoteVideoRenderer.current)) {
            track.attach(renderer);
          }
        }
        break;
    }
  }

  onDetachTrack(room: TwilioRoom, track: LocalTrack | RemoteTrack | null, participant: LocalParticipant | RemoteParticipant) {
    let renderer: HTMLMediaElement;
    switch (track?.kind) {
      case "audio":
        if (track instanceof LocalAudioTrack) {
          if ((renderer = this.appRefs.localAudioRenderer.current)) {
            track.detach(renderer);
            renderer.srcObject = null;
          }
        } else {
          if ((renderer = this.appRefs.remoteAudioRenderer.current)) {
            track.detach(renderer);
            renderer.srcObject = null;
          }
        }
        break;
      case "video":
        if (track instanceof LocalVideoTrack) {
          if ((renderer = this.appRefs.localVideoRenderer.current)) {
            track.detach(renderer);
            renderer.srcObject = null;
          }
        } else {
          if ((renderer = this.appRefs.remoteVideoRenderer.current)) {
            track.detach(renderer);
            renderer.srcObject = null;
          }
        }
        break;
    }
  }

  onDisconnected(room: TwilioRoom) {
  }


  componentDidUpdate(prevProps: Readonly<CallFragmentProps>, prevState: Readonly<CallFragmentState>, snapshot?: any) {
    if (prevState.permissionsState !== this.state.permissionsState && this.state.permissionsState === PermissionsState.GRANTED) {
      const call = this.calls.getCurrentCall();
      if (!call) {
        this.onCreate();
      } else if (call.creator !== getMemberAuth().getMemberId()) {
        this.onConnect();
      }
    }
    if (this.state.callType && prevState?.callType !== this.state.callType) {
      switch (this.state.callType) {
        case CallType.CREATE:
          playRingtone();
          break;
        default:
          if (prevState?.callType === CallType.CREATE) {
            stopRingtone();
          }
          break;
      }
    }
    if (prevState.minimized !== this.state.minimized) {
      this.props.listener.onMinimizedStateChanged(this.state.minimized);
    }
  }

  renderContainerContent(): React.ReactElement | null {
    return !this.state.minimized ? <Box
        style={{
          width: "100dvw",
          height: "100dvh",
          display: "flex",
          position: "absolute",
          background: black,
          zIndex: 10000,
        }}>
        <Box
          style={{
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: PD_MD,
          }}>
          {this.renderParticipant(true, this.appRefs.localAudioElement, this.appRefs.localVideoElement)}
          {this.renderParticipant(false, this.appRefs.remoteAudioElement, this.appRefs.remoteVideoElement)}
        </Box>
        <Box
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            position: "absolute",
            background: this.calls.getOtherParticipantCount() ? null : translucentBlack,
          }}>
          {this.state.permissionsState === PermissionsState.GRANTED
            ? this.renderCallContent()
            : (this.state.permissionsState === PermissionsState.NOT_GRANTED || this.state.permissionsState === PermissionsState.DENIED ?
              <PermissionsFragment
                permissions={[Permission.CAMERA_AND_MICROPHONE]}
                promptTitle="Permissions"
                promptText="Before we can start a call, we need some permissions from you."
                deniedTitle="Video chat"
                deniedText="We cannot start this call because you denied permissions."
                onGranted={() => {
                  this.setState({permissionsState: PermissionsState.GRANTED})
                }}
                onDenied={() => {
                  this.setState({permissionsState: PermissionsState.DENIED});
                  this.calls.endCurrentCall(CallEndReason.NO_PERMISSIONS);
                }}
                onDismiss={() => this.calls.clearCurrentCall()}
              />
              : null)
          }
        </Box>
      </Box>
      : <Box
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          position: "fixed",
          pointerEvents: "none",
          zIndex: 10000,
        }}>
        <Box
          onClick={() => this.maximize()}
          style={{
            pointerEvents: "auto",
            width: 180,
            height: 360,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            position: "absolute",
            bottom: PD_MD,
            right: PD_MD,
            gap: PD_XSM,
          }}>
          {this.renderParticipant(true, this.appRefs.localAudioElement, this.appRefs.localVideoElement)}
          {this.renderParticipant(false, this.appRefs.remoteAudioElement, this.appRefs.remoteVideoElement)}
        </Box>
      </Box>;
  }

  private renderParticipant(isLocal: boolean, audioElement: ReactElement, videoElement: ReactElement) {
    const call = this.calls.getCurrentCall();
    return <Card style={{
      background: nightGray,
      flexBasis: 0,
      flexGrow: 1,
      flexShrink: 0,
      aspectRatio: 1,
      display: call?.type !== CallType.END && (isLocal || this.calls.getOtherParticipantCount()) ? "block" : "none",
    }}>
      {audioElement}
      {videoElement}
    </Card>;
  }

  private renderCallContent() {
    const call = this.calls.getCurrentCall();
    switch (call?.type) {
      case CallType.CREATE:
        return this.renderCreateCall();
      case CallType.CONNECT:
        return this.renderConnectCall();
      case CallType.START:
        return this.renderStartCall();
      case CallType.END:
        return this.renderEndCall();
    }
    return null;
  }

  private renderCreateCall() {
    return this.renderOverlay(this.renderCreateCallContent());
  }

  private renderConnectCall() {
    return this.renderOverlay(this.renderConnectCallContent());
  }

  private renderStartCall() {
    return this.renderOverlay(this.renderStartCallContent(), "flex-end");
  }

  private renderEndCall() {
    return this.renderOverlay(this.renderEndCallContent());
  }

  private renderOverlay(content: any, justifyContent?: string) {
    return <Box style={{
      position: "absolute",
      left: PD_MD,
      right: PD_MD,
      top: PD_MD,
      bottom: PD_MD,
      margin: "auto",
      maxWidth: DW_LG,
      display: "flex",
      alignItems: "center",
      flexDirection: "column",
      gap: PD_LG,
    }} justifyContent={justifyContent || "center"}>
      {content}
    </Box>;
  }

  private renderCallButtons() {
    const call = this.calls.getCurrentCall();
    // const starting = call.type === CallType.CONNECT || call.type === CallType.START;
    return <>
      {this.renderCallButton(new Action("Disconnect", event => this.onDisconnect(), CallEndOutlined), colorRed, "white")}
      {/*{this.renderCallButton(new Action("Camera off", event => this.onToggleCameraOff(), VideocamOffOutlined), this.state.cameraOff?.selected ? white : mediumGray)}*/}
      {/*{this.renderCallButton(new Action("Mute", event => this.onToggleMuteOn(), MicOffOutlined), this.state.muteOn?.selected ? "white" : mediumGray)}*/}
      {/*{!isMe && !starting ?*/}
      {/*  this.renderCallButton(new Action("Connect", event => this.onConnect(), CallOutlined), colorGreen, "white")*/}
      {/*  : null}*/}
    </>;
  }

  private renderCreateCallContent() {
    return <>
      <Card style={{width: 240, aspectRatio: 1, background: white}}>
        <img
          //@ts-ignore
          src={this.props.thread.getProfilePhoto()}
          style={{width: "100%", height: "100%"}}/>
      </Card>
      <Box style={{
        display: "flex",
        alignItems: "center",
        flexDirection: "column",
        color: white,
        gap: PD_SM,
      }}>
        <Typography variant="h4">{this.props.thread.getDisplayName()}</Typography>
        <Typography>Calling...</Typography>
      </Box>
      <Box style={{
        display: "flex",
        gap: PD_LG,
      }}>
        {this.renderCallButtons()}
      </Box>
    </>;
  }

  private renderConnectCallContent() {
    return <>
      <Box style={{
        display: "flex",
        alignItems: "center",
        flexDirection: "column",
        color: white,
        gap: PD_SM,
      }}>
        <CircularProgress/>
        <Typography variant="h4">{this.props.thread.getDisplayName()}</Typography>
        <Typography>Connecting...</Typography>
      </Box>
      <Box style={{
        display: "flex",
        gap: PD_LG,
      }}>
        {this.renderCallButtons()}
      </Box>
    </>;
  }

  private renderStartCallContent() {
    return <>
      <Box style={{
        display: "flex",
        alignItems: "center",
        flexDirection: "column",
        color: white,
        gap: PD_SM,
      }}>
        <Typography variant="h4">{this.props.thread.getDisplayName()}</Typography>
        {/*<Fab variant="extended"*/}
        {/*     color="secondary"*/}
        {/*     style={{*/}
        {/*       padding: PD_MD,*/}
        {/*     }}*/}
        {/*     onClick={() => this.minimize()}>*/}
        {/*  <KeyboardArrowDownOutlined sx={{mr: 1}}/>*/}
        {/*  Minimize*/}
        {/*</Fab>*/}
      </Box>
      <Box style={{
        display: "flex",
        gap: PD_LG,
      }}>
        {this.renderCallButtons()}
      </Box>
    </>;
  }

  private renderEndCallContent() {
    const call = this.calls.getCurrentCall();
    const isMe = call.creator === getMemberAuth().getMemberId();
    const reason = call.endReason;
    return <>
      <Box style={{
        display: "flex",
        alignItems: "center",
        flexDirection: "column",
        color: white,
        gap: PD_SM,
      }}>
        <Typography variant="h4">{this.props.thread.getDisplayName()}</Typography>
        <Typography>{isMe ? CALL_END_REASON_TEXT_FROM_ME.get(reason) : CALL_END_REASON_TEXT_FROM_OTHER.get(reason)}</Typography>
      </Box>
      <Box style={{
        display: "flex",
        gap: PD_LG,
      }}>
        {this.renderEndCallButtons(reason)}
      </Box>
    </>;
  }

  private renderEndCallButtons(reason: CallEndReason) {
    const call = this.calls.getCurrentCall();
    const isMe = call.creator === getMemberAuth().getMemberId();
    switch (reason) {
      case CallEndReason.NO_ANSWER:
      case CallEndReason.TIME_OUT:
      case CallEndReason.BUSY:
      case CallEndReason.NAVIGATED_AWAY:
        return <>
          {this.renderCallButton(new Action("Dismiss", event => this.onDismiss(), CloseOutlined), white, black)}
          {this.renderCallButton(new Action(isMe ? "Call again" : "Call back", event => this.onCallAgain(), CallOutlined), colorGreen, "white")}
        </>;
    }
    return null;
  }

  private onCreate() {
    this.calls.createCall(this.props.message.callLog.channel, this);
  }

  private onConnect() {
    this.calls.connectAndStartCurrentCall(this);
  }

  private onDisconnect() {
    this.calls.endCurrentCall(CallEndReason.HANG_UP);
  }

  private onToggleCameraOff() {

  }

  private onToggleMuteOn() {

  }

  private onDismiss() {
    this.calls.clearCurrentCall();
  }

  private onCallAgain() {
    this.calls.clearCurrentCall();
    this.calls.createCall(this.props.message.callLog.channel, this);
  }

  private renderCallButton(action: Action, background: string, foreground: string = "black") {
    const IconType = action.iconType;
    return <Tooltip arrow title={action.text}>
      <Card style={{background: background}}>
        <ButtonBase style={{width: SZ_MD, height: SZ_MD}}
                    onClick={(event) => action.onClick(event)}>
          <IconType style={{fontSize: 36, color: foreground}}/>
        </ButtonBase>
      </Card>
    </Tooltip>;
  }
}
